/**
 * Build group leaderboard from group and its challenges.
 * Pure helper for testability (Liran's Rules).
 */

import type { Group, ChallengeWithStats } from '../../../types/challenges'
import type { DisplayAvatarId } from '../../avatars/types/avatar.types'
import { normalizeDisplayAvatarId } from '../../avatars/types/avatar.types'
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types'

export interface GroupLeaderboardEntry {
  userId: string
  name: string
  rank: number
  wins: number
  challengesPlayed: number
  totalPoints: number
  /** Net coins won (positive) or lost (negative) across all completed group challenges. */
  coinsDelta: number
  /** True when member is in friends list but friendship is pending (invitation sent, not yet accepted). */
  isPending?: boolean
  /** Avatar ID from friends list (for real avatar in leaderboard); always a valid display id. */
  avatarId: DisplayAvatarId
  /** Custom layered look from friends list; overrides avatarId when present. */
  avatarConfig?: EquippedMap | null
  /** Avatar URL from friends list (for real avatar in leaderboard). */
  avatar?: string
}

export interface BuildGroupLeaderboardOptions {
  /** Display names for members (e.g. from friends list). Used when not set from challenge leader. */
  memberNames?: Record<string, string>
  /** User IDs of members who are pending friends (invitation sent, not yet accepted). */
  pendingMemberIds?: Set<string>
  /** Avatar id/url per member (e.g. from friends list). */
  memberAvatars?: Record<
    string,
    { avatarId?: string | null; avatarConfig?: EquippedMap | null; avatar?: string }
  >
}

const ACCEPTED = 'ACCEPTED'

/** User IDs who accepted this challenge. If participantStatuses missing (legacy), treat all participantIds as accepted. */
export function getAcceptedParticipantIds(c: ChallengeWithStats): Set<string> {
  const statuses = c.participantStatuses
  if (statuses?.length) {
    return new Set(
      statuses.filter((p) => (p.status?.toUpperCase?.() ?? p.status) === ACCEPTED).map((p) => p.userId)
    )
  }
  return new Set(c.participantIds ?? [])
}

export function buildGroupLeaderboard(
  group: Group,
  challenges: ChallengeWithStats[],
  options?: BuildGroupLeaderboardOptions
): GroupLeaderboardEntry[] {
  const byGroup = challenges.filter(
    (c) => c.groupId === group.id && (c.status?.toLowerCase?.() ?? c.status) === 'completed'
  )
  const memberIds = group.memberIds ?? []
  const memberNames = options?.memberNames
  const pendingMemberIds = options?.pendingMemberIds ?? new Set<string>()
  const memberAvatars = options?.memberAvatars

  const wins: Record<string, number> = {}
  const played: Record<string, number> = {}
  const points: Record<string, number> = {}
  const coins: Record<string, number> = {}
  const names: Record<string, string> = {}

  memberIds.forEach((id) => {
    wins[id] = 0
    played[id] = 0
    points[id] = 0
    coins[id] = 0
  })

  byGroup.forEach((c) => {
    const acceptedIds = getAcceptedParticipantIds(c)
    const acceptedMembers = memberIds.filter((id) => acceptedIds.has(id))
    memberIds.forEach((id) => {
      if (acceptedIds.has(id)) {
        played[id] += 1
        if (c.leader?.userId === id) wins[id] += 1
      }
    })
    // Coins: winner gains stake * (participants - 1), each loser loses their stake
    const stake = c.stakeCoins ?? 0
    if (stake > 0 && acceptedMembers.length >= 2) {
      const winnerId = c.leader?.userId
      acceptedMembers.forEach((id) => {
        if (id === winnerId) {
          coins[id] = (coins[id] ?? 0) + stake * (acceptedMembers.length - 1)
        } else {
          coins[id] = (coins[id] ?? 0) - stake
        }
      })
    }
    c.picks?.forEach((p: { userId: string; pointsEarned?: number }) => {
      if (memberIds.includes(p.userId)) {
        points[p.userId] = (points[p.userId] ?? 0) + (p.pointsEarned ?? 0)
      }
    })
    if (c.leader?.userId && memberIds.includes(c.leader.userId)) {
      names[c.leader.userId] = c.leader.userName
    }
  })

  const entries: GroupLeaderboardEntry[] = memberIds.map((memberId) => {
    const av = memberAvatars?.[memberId]
    return {
      userId: memberId,
      name: names[memberId] ?? memberNames?.[memberId] ?? 'Member',
      rank: 0,
      wins: wins[memberId] ?? 0,
      challengesPlayed: played[memberId] ?? 0,
      totalPoints: points[memberId] ?? 0,
      coinsDelta: coins[memberId] ?? 0,
      isPending: pendingMemberIds.has(memberId),
      avatarId: normalizeDisplayAvatarId(av?.avatarId),
      avatarConfig: av?.avatarConfig ?? null,
      avatar: av?.avatar,
    }
  })

  // Append pending group invitees (invited to group but not yet accepted)
  const pendingInvitees = group.pendingInvitees ?? []
  pendingInvitees.forEach((p) => {
    const av = memberAvatars?.[p.id]
    entries.push({
      userId: p.id,
      name: p.name ?? memberNames?.[p.id] ?? 'Pending',
      rank: 0,
      wins: 0,
      challengesPlayed: 0,
      totalPoints: 0,
      coinsDelta: 0,
      isPending: true,
      avatarId: normalizeDisplayAvatarId(av?.avatarId ?? p.avatarId),
      avatarConfig: av?.avatarConfig ?? null,
      avatar: av?.avatar,
    })
  })

  // Members first (by wins desc, points desc, userId tie-break); pending invitees at the bottom
  entries.sort((a, b) => {
    const aPending = a.isPending === true
    const bPending = b.isPending === true
    if (aPending !== bPending) return aPending ? 1 : -1
    if (b.coinsDelta !== a.coinsDelta) return b.coinsDelta - a.coinsDelta
    if (b.wins !== a.wins) return b.wins - a.wins
    if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints
    return String(a.userId).localeCompare(String(b.userId))
  })
  entries.forEach((e, i) => {
    e.rank = i + 1
  })
  return entries
}
